import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, Filter, X, Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AuditLog() {
  const { isAdmin } = useAuth();
  const [filters, setFilters] = useState({ q: "", benutzer: "", feld: "", datum_von: "", datum_bis: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/audit-logs", { params });
      setItems(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filters); /* eslint-disable-next-line */ }, []);

  const reset = () => {
    const empty = { q: "", benutzer: "", feld: "", datum_von: "", datum_bis: "" };
    setFilters(empty); load(empty);
  };

  const exportCsv = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/export/audit-logs.csv", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "aenderungsprotokoll.csv"; a.click();
  };

  const uniqueBenutzer = useMemo(() => Array.from(new Set(items.map((l) => l.benutzer).filter(Boolean))).sort(), [items]);

  return (
    <div data-testid="audit-page">
      <PageHeader
        title="Änderungsprotokoll"
        subtitle={`${items.length} Einträge · Unveränderlich · Alle Änderungen automatisch protokolliert`}
        actions={isAdmin && (
          <Button onClick={exportCsv} className="h-12 px-5 bg-[#0A2540] hover:bg-[#113A63] text-white" data-testid="audit-export-btn">
            <Download className="h-4 w-4 mr-2" /> CSV-Export
          </Button>
        )}
      />

      <form
        onSubmit={(e) => { e.preventDefault(); load(filters); }}
        className="bg-white border rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end"
        style={{ borderColor: "#E2E8F0" }}
      >
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Volltextsuche</Label>
          <div className="relative mt-1.5">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-11 pl-10"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Benutzer, Auftragsnr., Feld, Werte…"
              data-testid="audit-q"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Benutzer</Label>
          <Input
            className="h-11 mt-1.5"
            value={filters.benutzer}
            onChange={(e) => setFilters({ ...filters, benutzer: e.target.value })}
            list="audit-benutzer-list"
            data-testid="audit-benutzer"
          />
          <datalist id="audit-benutzer-list">
            {uniqueBenutzer.map((b) => <option key={b} value={b} />)}
          </datalist>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Feld</Label>
          <Input
            className="h-11 mt-1.5"
            value={filters.feld}
            onChange={(e) => setFilters({ ...filters, feld: e.target.value })}
            placeholder="z. B. status, aufmass, login"
            data-testid="audit-feld"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Von</Label>
          <Input type="date" className="h-11 mt-1.5" value={filters.datum_von} onChange={(e) => setFilters({ ...filters, datum_von: e.target.value })} data-testid="audit-von" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Bis</Label>
          <Input type="date" className="h-11 mt-1.5" value={filters.datum_bis} onChange={(e) => setFilters({ ...filters, datum_bis: e.target.value })} data-testid="audit-bis" />
        </div>
        <div className="md:col-span-6 flex gap-2">
          <Button type="submit" className="h-11 px-6 bg-[#0A2540] hover:bg-[#113A63] text-white" data-testid="audit-filter-btn">
            <Filter className="h-4 w-4 mr-2" /> Filtern
          </Button>
          <Button type="button" variant="outline" className="h-11" onClick={reset} data-testid="audit-reset-btn">
            <X className="h-4 w-4 mr-2" /> Zurücksetzen
          </Button>
        </div>
      </form>

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 w-40">Datum</th>
              <th className="text-left px-4 py-3">Benutzer</th>
              <th className="text-left px-4 py-3">Referenz</th>
              <th className="text-left px-4 py-3">Feld</th>
              <th className="text-left px-4 py-3">Alter Wert</th>
              <th className="text-left px-4 py-3">Neuer Wert</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-10 text-center text-slate-400">Lädt…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">Keine Einträge</td></tr>}
            {items.map((l) => (
              <tr key={l.id} className="border-t hover:bg-slate-50" style={{ borderColor: "#E2E8F0" }} data-testid={`audit-row-${l.id}`}>
                <td className="px-4 py-2.5 mono text-xs">{l.datum?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2.5 font-semibold">{l.benutzer}</td>
                <td className="px-4 py-2.5 text-slate-600">{l.auftragsnummer}</td>
                <td className="px-4 py-2.5">
                  <span className="text-xs font-semibold uppercase tracking-wider bg-slate-100 rounded px-2 py-0.5">{l.feld}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-500 mono text-xs truncate max-w-[240px]">{l.alter_wert}</td>
                <td className="px-4 py-2.5 mono text-xs truncate max-w-[240px]">{l.neuer_wert}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ActionBar onPrint={() => window.print()} onExport={isAdmin ? exportCsv : undefined} hideNext />
    </div>
  );
}
